import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import PasswordInput from "../components/PasswordInput";

const BG = "https://images.unsplash.com/photo-1505144808419-1957a94ca61e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const roleHome = (role) => role === "hr" ? "/espace-entreprise" : "/dashboard";

export default function Login() {
  const { login, user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      // Admins should authenticate from the dedicated admin entry point.
      nav("/acces-admin", { replace: true });
      return;
    }
    if (user.must_change_password) nav("/changer-mot-de-passe");
    else nav(roleHome(user.role));
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin") {
        setErr("Cet espace est réservé aux coachés accompagnés. Merci d'utiliser l'accès dédié.");
        return;
      }
      if (u.must_change_password) nav("/changer-mot-de-passe");
      else nav(roleHome(u.role));
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout noFooter>
      <section className="relative min-h-screen flex">
        <div className="hidden md:block w-1/2 relative">
          <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover editorial-img" />
          <div className="absolute inset-0 bg-navy/55"></div>
          <div className="relative z-10 h-full flex flex-col justify-between p-12 text-ivory">
            <div></div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-6">— Espace privé</div>
              <p className="font-serif text-4xl lg:text-5xl font-light leading-[1.05] max-w-md">Votre accompagnement,<br/><span className="italic">en toute confidentialité.</span></p>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 md:px-12 py-32">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-4xl md:text-5xl font-light text-navy leading-tight mb-10">{t.auth.welcome || "Bienvenue"}</h1>

            <form onSubmit={submit} data-testid="login-form" className="space-y-7">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{t.auth.email}</label>
                <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand transition-colors" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">{t.auth.password}</label>
                <PasswordInput data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              {err && <div data-testid="login-error" className="text-sm text-destructive">{err || t.auth.error}</div>}
              <button data-testid="login-submit" disabled={loading} type="submit" className="w-full bg-brand hover:bg-brandDark disabled:opacity-60 text-white py-4 text-sm uppercase tracking-[0.18em] font-mono">
                {loading ? "..." : t.auth.submit}
              </button>
              <div className="text-right">
                <Link to="/mot-de-passe-oublie" data-testid="forgot-link" className="text-xs text-ink/55 hover:text-brand link-underline">{t.auth.forgot}</Link>
              </div>
            </form>

            <div className="mt-12 pt-8 border-t border-black/10 text-xs text-ink/50">
              <Link to="/" className="link-underline">← {t.nav.home}</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
